// src/components/HomePageSubscriptions.tsx
import React, { useMemo } from 'react';
import SectionCard from './SectionCard';
import { AppstoreOutlined } from '@ant-design/icons';
import {
  Avatar,
  Badge,
  Button,
  Empty,
  Flex,
  Space,
  Tag,
  Typography,
  theme,
} from 'antd';
import { useLanguage } from 'src/providers/LanguageProvider';
import { ILicenseSubscription } from 'src/types/types';
import { useNavigate } from 'react-router';

const { Text } = Typography;

type Props = {
  subscriptions: ILicenseSubscription[];
  /** Para formatı için opsiyonel override; varsayılan EUR kalır. */
  currencyCode?: string;  // default: 'EUR'
  /** Ürün görseli için tablo sayfasındaki gibi: ürüne göre URL döndür. */
  getImage?: (sub: ILicenseSubscription) => string | undefined | null;
  /** “Tümünü gör” tıklanınca ne olsun */
  onViewAll?: () => void;
};

type RowStatus = 'active' | 'canceled';

const statusMetaKey: Record<RowStatus, { color: 'success' | 'default'; i18n: string }> = {
  active:   { color: 'success', i18n: 'ACTIVE' },
  canceled: { color: 'default', i18n: 'CANCELED' },
};

const localeMap: Record<string, string> = {
  tr: 'tr-TR',
  en: 'en-US',
  de: 'de-DE',
  nl: 'nl-NL',
};

function fmtMoney(
  n: number | string | null | undefined,
  locale: string,
  currency: string
) {
  const val = Number(n ?? 0);
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(val);
  } catch {
    return new Intl.NumberFormat(locale).format(val);
  }
}

/** Sadece üst lisansları döndürür (ParentLicenseId === 0) */
function topLevel(subs: ILicenseSubscription[]) {
  return (subs || []).filter((s) => s.ParentLicenseId === 0);
}

const HomePageSubscriptions: React.FC<Props> = ({
  subscriptions,
  currencyCode = 'EUR',        // ⬅️ her zaman EURO
  getImage,
  onViewAll,
}) => {
  const { t, language } = useLanguage();
  const { token } = theme.useToken();

  const locale = localeMap[language] || language || 'en-US'; // dil → locale (para biriminden bağımsız)

  const roots = useMemo(() => topLevel(subscriptions ?? []), [subscriptions]);
  const count = roots.length;
  const items = roots.slice(0, 3); // anasayfa için en fazla 3 satır göster

  const navigate = useNavigate();

  return (
    <SectionCard
      icon={<AppstoreOutlined />}
      title={t('SUBSCRIPTIONS')}
      extra={
        count > 3 ? (
          <Button
            type="text"
            size="small"
            onClick={() => (onViewAll ? onViewAll() : navigate('/subscriptions'))}
            aria-label={t('ARIA_VIEW_ALL_SUBSCRIPTIONS')}
          >
            {t('VIEW_ALL')}
          </Button>
        ) : null
      }
    >
      <div>
        {count === 0 ? (
          <Empty
            description={t('NO_SUBS')}
            imageStyle={{ height: 60 }}
            style={{ marginBlock: 8 }}
          />
        ) : (
          <Space direction="vertical" style={{ width: '100%' }} size={10}>
            {items.map((sub) => {
              const status: RowStatus = sub.IsActive ? 'active' : 'canceled';
              const statusMeta = statusMetaKey[status];
              const hasChildrenTotal = true;

              const img = getImage?.(sub);
              const interval = sub.LicenseProductIsMonthly
                ? t('MONTHLY')
                : t('YEARLY');

              const unitPrice = fmtMoney(sub.LicenseProductPrice, locale, currencyCode);
              const totalPrice = fmtMoney(sub.totalAmount, locale, currencyCode);

              return (
                <Flex
                  key={sub.Id}
                  align="center"
                  justify="space-between"
                  style={{
                    width: '100%',
                    padding: 10,
                    borderRadius: 10,
                    background: token.colorBgContainer,
                  }}
                  gap={12}
                >
                  {/* Sol: görsel + metinler */}
                  <Flex align="center" gap={12} style={{ minWidth: 0 }}>
                    <Badge color={statusMeta.color} dot offset={[-2, 30]}>
                      <Avatar
                        shape="square"
                        size={40}
                        src={img || `https://api.dicebear.com/8.x/shapes/svg?seed=${encodeURIComponent(sub?.LicenseProductId)}`}
                        alt={sub.LicenseProductName}
                        style={{ flex: '0 0 auto' }}
                      >
                        {sub.LicenseProductName?.[0]?.toUpperCase() || '•'}
                      </Avatar>
                    </Badge>

                    <div style={{ minWidth: 0 }}>
                      <Text strong ellipsis style={{ display: 'block', maxWidth: 280 }}>
                        {sub.LicenseProductName}
                      </Text>

                      <Space size={8} wrap>
                        <Tag bordered={false}>{interval}</Tag>

                        {!hasChildrenTotal ? (
                          <Tag bordered={false}>
                            {unitPrice} / {interval}
                          </Tag>
                        ) : (
                          <>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {unitPrice} / {interval}
                            </Text>
                            <Tag bordered={false} color="blue">
                              <Text strong>
                                {totalPrice} / {interval}
                              </Text>
                            </Tag>
                          </>
                        )}

                        <Tag bordered={false} color={statusMeta.color}>
                          {t(statusMeta.i18n, status === 'active' ? 'Aktif' : 'İptal')}
                        </Tag>
                      </Space>
                    </div>
                  </Flex>
                </Flex>
              );
            })}
          </Space>
        )}
      </div>
    </SectionCard>
  );
};

export default HomePageSubscriptions;
